/**
 * Tabela de preços de modelos de IA — agrupada por model/token_type.
 *
 * Exibe o preço vigente (effective_to === null) para cada par model/token_type,
 * com histórico recolhível e formulário de novo preço (append-only).
 */
import { useState } from 'react';
import { Button } from '../../design-system/components/Button';
import { Card } from '../../design-system/components/Card';
import { Select } from '../../design-system/components/Select';
import { TextField } from '../../design-system/components/TextField';
import { Spinner } from '../../design-system/components/Spinner';
import { ErrorState } from '../../shared/components/ErrorState';
import { EmptyState } from '../../shared/components/EmptyState';
import { useModelPrices, useCreateModelPrice } from './hooks';
import type { ModelPriceCreate, ModelPricePublic } from './types';

const TOKEN_TYPES = ['input_text', 'input_audio', 'cached_input', 'output_text'] as const;

const TOKEN_TYPE_LABELS: Record<string, string> = {
  input_text: 'Texto de entrada',
  input_audio: 'Áudio de entrada',
  cached_input: 'Entrada em cache',
  output_text: 'Texto de saída',
};
const TOKEN_TYPE_OPTIONS = TOKEN_TYPES.map((tt) => ({
  value: tt,
  label: TOKEN_TYPE_LABELS[tt] ?? tt,
}));

interface GroupKey {
  model: string;
  token_type: string;
}

interface PriceGroup {
  key: GroupKey;
  vigente: ModelPricePublic | null;
  historico: ModelPricePublic[];
}

function groupPrices(prices: ModelPricePublic[]): PriceGroup[] {
  const map = new Map<string, PriceGroup>();

  for (const price of prices) {
    const k = `${price.model}::${price.token_type}`;
    if (!map.has(k)) {
      map.set(k, {
        key: { model: price.model, token_type: price.token_type },
        vigente: null,
        historico: [],
      });
    }
    const group = map.get(k)!;
    if (price.effective_to === null) {
      group.vigente = price;
    } else {
      group.historico.push(price);
    }
  }

  // Ordenar histórico do mais recente para o mais antigo
  for (const group of map.values()) {
    group.historico.sort(
      (a, b) => new Date(b.effective_from).getTime() - new Date(a.effective_from).getTime(),
    );
  }

  return Array.from(map.values()).sort((a, b) =>
    `${a.key.model}${a.key.token_type}`.localeCompare(`${b.key.model}${b.key.token_type}`),
  );
}

interface PriceGroupRowProps {
  group: PriceGroup;
}

function PriceGroupRow({ group }: PriceGroupRowProps) {
  const [historicoAberto, setHistoricoAberto] = useState(false);

  return (
    <>
      <tr>
        <td>{group.key.model}</td>
        <td>{group.key.token_type}</td>
        <td>
          {group.vigente !== null ? group.vigente.price_usd : <span className="muted">—</span>}
        </td>
        <td>{group.vigente?.note ?? <span className="muted">—</span>}</td>
        <td>
          {group.historico.length > 0 && (
            <button
              type="button"
              className="billing-prices__history-toggle"
              onClick={() => setHistoricoAberto((v) => !v)}
              aria-expanded={historicoAberto}
            >
              {historicoAberto ? 'Ocultar histórico' : `Ver histórico (${group.historico.length})`}
            </button>
          )}
        </td>
      </tr>
      {historicoAberto &&
        group.historico.map((h) => (
          <tr key={h.effective_from} className="billing-prices__history-row">
            <td colSpan={2}>
              <span className="muted">Histórico</span>
            </td>
            <td>{h.price_usd}</td>
            <td>{h.note ?? <span className="muted">—</span>}</td>
            <td>
              <span className="muted">
                até {new Date(h.effective_to!).toLocaleDateString('pt-BR')}
              </span>
            </td>
          </tr>
        ))}
    </>
  );
}

interface NovoPrecoFormState {
  model: string;
  token_type: string;
  price_usd: string;
  note: string;
}

function emptyNovoPreco(): NovoPrecoFormState {
  return { model: '', token_type: 'input_text', price_usd: '', note: '' };
}

interface NovoPrecoFormProps {
  onSave: (form: NovoPrecoFormState) => void;
  isSaving: boolean;
  error: unknown;
  validacao: string | null;
  saved: boolean;
}

function NovoPrecoForm({ onSave, isSaving, error, validacao, saved }: NovoPrecoFormProps) {
  const [form, setForm] = useState<NovoPrecoFormState>(emptyNovoPreco);

  function set(field: keyof NovoPrecoFormState, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  return (
    <div className="billing-prices__novo-preco">
      <h3 className="dash-section__title">Novo preço</h3>
      {validacao !== null && (
        <span className="label" role="alert">
          {validacao}
        </span>
      )}
      {error !== null && error !== undefined && <ErrorState error={error} />}
      <div className="dash-grid">
        <TextField
          label="Modelo"
          value={form.model}
          onChange={(e) => set('model', e.target.value)}
          required
          helperText="Ex: gemini-2.0-flash-lite"
        />
        <Select
          label="Tipo de token"
          options={TOKEN_TYPE_OPTIONS}
          value={form.token_type}
          onChange={(e) => set('token_type', e.target.value)}
          required
        />
        <TextField
          label="Preço USD / M tokens"
          type="number"
          step="0.0001"
          min="0"
          value={form.price_usd}
          onChange={(e) => set('price_usd', e.target.value)}
          required
          helperText="Valor por milhão de tokens"
        />
        <TextField
          label="Nota"
          value={form.note}
          onChange={(e) => set('note', e.target.value)}
          helperText="Descrição opcional"
        />
      </div>
      <div className="row row--sm-gap billing-admin__row-mt-md">
        <Button onClick={() => onSave(form)} isLoading={isSaving} disabled={isSaving}>
          Registrar preço
        </Button>
        {saved && (
          <span className="label" role="status" aria-live="polite">
            Preço registrado com sucesso.
          </span>
        )}
      </div>
    </div>
  );
}

export function ModelPricesTable() {
  const query = useModelPrices();
  const createMutation = useCreateModelPrice();
  const [formError, setFormError] = useState<unknown>(null);
  const [formValidacao, setFormValidacao] = useState<string | null>(null);
  const [formKey, setFormKey] = useState(0);
  const [saved, setSaved] = useState(false);

  function handleSave(form: NovoPrecoFormState) {
    setFormError(null);
    setFormValidacao(null);
    setSaved(false);
    const priceUsd = parseFloat(form.price_usd);
    if (form.model.trim() === '') {
      setFormValidacao('Informe o identificador do modelo.');
      return;
    }
    if (!(priceUsd > 0)) {
      setFormValidacao('Informe um preço em USD maior que zero.');
      return;
    }
    const payload: ModelPriceCreate = {
      model: form.model.trim(),
      token_type: form.token_type,
      price_usd: priceUsd,
      note: form.note.length > 0 ? form.note : null,
    };
    createMutation.mutate(payload, {
      onSuccess: () => {
        // Remonta o formulário para limpar os campos e evitar re-submissão duplicada.
        setFormKey((k) => k + 1);
        setSaved(true);
      },
      onError: (err) => setFormError(err),
    });
  }

  if (query.isPending) {
    return (
      <div className="center">
        <Spinner label="Carregando preços de modelo" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <ErrorState
        error={query.error}
        onRetry={() => query.refetch()}
        title="Não foi possível carregar os preços de modelo"
      />
    );
  }

  const prices = query.data ?? [];
  const groups = groupPrices(prices);

  return (
    <Card>
      <h2 className="dash-section__title">Preços de modelos de IA</h2>
      <p className="muted">
        Preço por milhão de tokens por modelo e tipo. Cada entrada é imutável (append-only).
      </p>
      {groups.length === 0 ? (
        <EmptyState
          title="Nenhum preço cadastrado"
          description="Registre o primeiro preço para começar."
        />
      ) : (
        <div className="billing-admin__scroll">
          <table className="dash-table">
            <thead>
              <tr>
                <th>Modelo</th>
                <th>Tipo de token</th>
                <th>Preço USD/M</th>
                <th>Nota</th>
                <th>Histórico</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <PriceGroupRow key={`${group.key.model}::${group.key.token_type}`} group={group} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <NovoPrecoForm
        key={formKey}
        onSave={handleSave}
        isSaving={createMutation.isPending}
        error={formError}
        validacao={formValidacao}
        saved={saved}
      />
    </Card>
  );
}
